/** @format */

import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { _$, dayjs, Icon } from '@king-design/react'
import { SessionDetailSessionTrace } from './typescript'
import { secondsFormat } from 'src/utils/common'
import variable from 'src/static/styles/var'
import { tokenValueFormat } from '../../constant'
import { CopyButton } from 'src/components'

interface IoSectionProps {
  label: string
  text?: string
  expanded: boolean
  onToggle: () => void
}

const IoSection: React.FC<IoSectionProps> = ({ label, text, expanded, onToggle }) => {
  const displayText = text || '-'
  const togglingRef = useRef(false)

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (togglingRef.current) {
        return
      }
      togglingRef.current = true
      onToggle()
      requestAnimationFrame(() => {
        togglingRef.current = false
      })
    },
    [onToggle]
  )

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('.span-item__io-copy')) {
        return
      }
      handleToggle(e)
    },
    [handleToggle]
  )

  return (
    <section className="span-item__io">
      <div
        onClick={handleRowClick}
        className={['span-item__io-row', expanded ? 'is-expanded' : ''].filter(Boolean).join(' ')}
      >
        <button type="button" className="span-item__io-toggle" aria-expanded={expanded}>
          <Icon className="k-icon-up" size={12} aria-hidden />
        </button>
        <span className="span-item__io-label">{label}</span>
        {!expanded ? <span className="span-item__io-preview">{displayText}</span> : null}
        {text ? (
          <span className="span-item__io-copy">
            <CopyButton text={displayText} />
          </span>
        ) : (
          ''
        )}
      </div>
      {expanded ? <div className="span-item__io-content">{displayText}</div> : null}
    </section>
  )
}

export interface SpanItemProps {
  className?: string
  trace: SessionDetailSessionTrace
  expand: boolean
  toTraceDetail: (trace: { TraceId: string; StartTime: string }) => void
  /** 时间轴锚点选中时高亮 */
  active?: boolean
  index: number
  onExpandChange: () => void
}

const SpanItem: React.FC<SpanItemProps> = ({
  className,
  index,
  trace,
  toTraceDetail,
  expand,
  active,
  onExpandChange
}) => {
  const [inputExpand, setInputExpand] = useState(false)
  const [outputExpand, setOutputExpand] = useState(false)

  const handleHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onExpandChange()
    },
    [onExpandChange]
  )

  return (
    <article
      className={[className, active ? 'span-item--active' : ''].filter(Boolean).join(' ')}
      data-trace-id={trace?.TraceId}
    >
      <header className="span-item__header" onClick={handleHeaderClick}>
        <Icon
          className={`k-icon-up span-item__chevron ${expand ? 'is-expanded' : ''}`}
          size={14}
          aria-hidden
        />
        <span className="span-item__title">{_$('第{x}条调用链', { x: index + 1 })}</span>
        <span className="span-item__time">
          {dayjs(trace?.StartTime).format('YYYY-MM-DD HH:mm:ss')}
        </span>
      </header>

      <div className={`span-item__body-wrap ${expand ? 'is-expanded' : ''}`} aria-hidden={!expand}>
        <div className="span-item__body-inner">
          <div className="span-item__panel">
            <IoSection
              label="Input"
              text={trace?.Input}
              expanded={inputExpand}
              onToggle={() => setInputExpand(prev => !prev)}
            />
            <IoSection
              label="Output"
              text={trace?.Output}
              expanded={outputExpand}
              onToggle={() => setOutputExpand(prev => !prev)}
            />
          </div>

          <footer className="span-item__meta">
            <span className="span-item__meta-item">
              <span className="span-item__meta-label">{_$('TraceID')}</span>
              <a
                className="span-item__meta-link"
                onClick={e => {
                  e.preventDefault()
                  toTraceDetail({
                    TraceId: trace?.TraceId,
                    StartTime: trace?.StartTime
                  })
                }}
              >
                {trace?.TraceId}
              </a>
            </span>
            <span className="span-item__meta-item">
              <span className="span-item__meta-label">{_$('耗时')}</span>
              <span className="span-item__meta-value">{secondsFormat(trace?.DurationMs)}</span>
            </span>
            <span className="span-item__meta-item">
              <span className="span-item__meta-label">Total Tokens</span>
              <span className="span-item__meta-value">{tokenValueFormat(trace?.TotalTokens)}</span>
            </span>
          </footer>
        </div>
      </div>
    </article>
  )
}

export default styled(SpanItem)`
  box-sizing: border-box;
  margin-bottom: 24px;
  scroll-margin-top: 80px;
  &:last-child {
    margin-bottom: 0;
  }

  .span-item__header {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  }

  .span-item__chevron {
    flex-shrink: 0;
    color: ${variable.descColor};
    transition: transform 0.28s ease;
    transform: rotate(180deg);

    &.is-expanded {
      transform: rotate(0deg);
    }
  }

  .span-item__title {
    flex-shrink: 0;
    font-size: 12px;
    line-height: 18px;
  }

  .span-item__time {
    color: #151b1e;
    font-size: 12px;
    line-height: 18px;
  }

  .span-item__body-wrap {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    margin-top: 8px;
    transition: grid-template-rows 0.28s ease, opacity 0.22s ease;

    &:not(.is-expanded) {
      opacity: 0;
      pointer-events: none;
      height: 0;
      margin-top: 0;
    }

    &.is-expanded {
      grid-template-rows: 1fr;
      opacity: 1;
    }
  }

  .span-item__body-inner {
    min-height: 0;
    overflow: hidden;
    border: 1px solid ${variable.borderColor};
    border-radius: 6px;
    padding: 12px;
    background: #fff;
    transition: border-color 0.2s ease;
  }

  &.span-item--active .span-item__body-inner {
    animation: span-item-body-inner 3s linear forwards;
  }

  @keyframes span-item-body-inner {
    0%,
    30%,
    60% {
      border-color: ${variable.primaryColor};
    }
    15%,
    45%,
    75%,
    100% {
      border-color: ${variable.borderColor};
    }
  }

  .span-item__panel {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .span-item__io {
    border-radius: 4px;
    background: #f8f9fa;
    overflow: hidden;
    padding: 8px 12px;
  }

  .span-item__io-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 36px;
    padding: 8px 0;

    &.is-expanded .span-item__io-toggle {
      transform: rotate(0deg);
    }
  }

  .span-item__io-toggle {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${variable.descColor};
    transform: rotate(180deg);
    transition: transform 0.28s ease;
  }

  .span-item__io-label {
    flex-shrink: 0;
    font-size: 14px;
    line-height: 22px;
  }

  .span-item__io-preview {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    line-height: 20px;
    color: ${variable.descColor};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .span-item__io-copy {
    flex-shrink: 0;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
  }

  .span-item__io-content {
    border-top: 1px solid ${variable.borderColor};
    padding: 8px 0 8px 24px;
    font-size: 12px;
    line-height: 20px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .span-item__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 32px;
    margin-top: 12px;
    font-size: 12px;
    line-height: 20px;
  }

  .span-item__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .span-item__meta-label {
    flex-shrink: 0;
    color: ${variable.descColor};
  }

  .span-item__meta-value {
    color: rgba(0, 0, 0, 0.88);
    word-break: break-all;
  }

  .span-item__meta-link {
    color: ${variable.primaryColor};
    text-decoration: none;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .span-item__body-wrap,
    .span-item__chevron,
    .span-item__io-toggle {
      transition: none;
    }

    &.span-item--active .span-item__panel {
      animation: none;
      border-color: ${variable.primaryColor};
    }
  }
`
